import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { ComposedChart, Line, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from "recharts";
import { Check } from "lucide-react";
import { formatNumber } from "../../lib/formatNumber";

// Screener.in-style share-price chart: range buttons, Price / PE Ratio views,
// price line on the right axis, volume (or EPS) bars on the left axis, and
// toggleable series checkboxes underneath. Data comes from the backend's
// /investors/stock-chart (Screener's chart API, Yahoo Finance fallback).
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
const API = `${BACKEND_URL}/api`;

const RANGES = [
  { key: "1m", label: "1M" },
  { key: "6m", label: "6M" },
  { key: "1y", label: "1Yr" },
  { key: "3y", label: "3Yr" },
  { key: "5y", label: "5Yr" },
  { key: "10y", label: "10Yr" },
  { key: "max", label: "Max" },
];

const METRICS = [
  { key: "price", label: "Price" },
  { key: "pe", label: "PE Ratio" },
];

const LINE_COLORS = ["#5b50e6", "#f59e0b", "#10b981"];
const BAR_COLOR = "#b3c7fb";
const isBarSeries = (key) => /volume|eps/i.test(key);
// Series hidden by default, like Screener (DMAs start unticked).
const DEFAULT_OFF = new Set(["DMA50", "DMA200"]);

const AXIS_TICK = { fill: "hsl(var(--muted-foreground))", fontSize: 12 };

const parseDate = (d) => new Date(`${d}T00:00:00`);
const formatTick = (d, range) =>
  parseDate(d).toLocaleDateString("en-GB", range === "1m" ? { day: "numeric", month: "short" } : { month: "short", year: "numeric" });

const formatVolume = (v) => (v >= 1000 ? `${formatNumber(Math.round(v / 1000))}k` : formatNumber(v));

const ChartTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="rounded-lg border border-border bg-card px-3 py-2 text-xs shadow-lg">
      <p className="font-semibold text-foreground mb-1">
        {parseDate(label).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })}
      </p>
      {payload.map((p) => (
        <p key={p.dataKey} className="text-muted-foreground">
          <span className="inline-block w-2 h-2 rounded-full mr-1.5" style={{ background: p.color }} />
          {p.name}: <span className="text-foreground font-medium">{formatNumber(p.value)}</span>
        </p>
      ))}
    </div>
  );
};

const Segmented = ({ options, value, onChange }) => (
  <div className="inline-flex max-w-full rounded-md border border-border overflow-x-auto">
    {options.map((o) => (
      <button
        key={o.key}
        type="button"
        onClick={() => onChange(o.key)}
        className={`shrink-0 px-2.5 sm:px-4 py-2 text-sm font-medium transition-colors ${
          value === o.key ? "bg-qcyan/10 text-qcyan" : "text-muted-foreground hover:text-foreground hover:bg-muted/40"
        }`}
      >
        {o.label}
      </button>
    ))}
  </div>
);

const StockChart = () => {
  const [range, setRange] = useState("1y");
  const [metric, setMetric] = useState("price");
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [hidden, setHidden] = useState(DEFAULT_OFF);

  useEffect(() => {
    let cancelled = false;
    setError(null);
    axios
      .get(`${API}/investors/stock-chart`, { params: { range, metric } })
      .then((res) => !cancelled && setData(res.data))
      .catch((err) => {
        if (cancelled) return;
        const detail = err.response?.data?.detail || err.message;
        setError(`Couldn't load the share price chart: ${detail}`);
        setData(null);
      });
    return () => {
      cancelled = true;
    };
  }, [range, metric]);

  const series = useMemo(() => data?.series || [], [data]);
  const lineSeries = series.filter((s) => !isBarSeries(s.key));
  const barSeries = series.filter((s) => isBarSeries(s.key));

  const toggle = (key) =>
    setHidden((prev) => {
      const next = new Set(prev);
      next.has(key) ? next.delete(key) : next.add(key);
      return next;
    });

  const points = data?.points || [];

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <Segmented options={RANGES} value={range} onChange={setRange} />
        <Segmented options={METRICS} value={metric} onChange={setMetric} />
      </div>

      {error && <p className="text-sm text-red-500 py-16 text-center">{error}</p>}
      {!error && !data && <p className="text-sm text-muted-foreground py-16 text-center">Loading chart…</p>}

      {!error && data && (
        <>
          <div className="h-[340px] sm:h-[420px]">
            <ResponsiveContainer width="100%" height="100%">
              <ComposedChart data={points} margin={{ top: 10, right: 0, bottom: 0, left: 0 }}>
                <CartesianGrid vertical={false} stroke="hsl(var(--border))" />
                <XAxis
                  dataKey="date"
                  tick={AXIS_TICK}
                  tickLine={false}
                  axisLine={{ stroke: "hsl(var(--border))" }}
                  interval="preserveStartEnd"
                  tickFormatter={(d) => formatTick(d, range)}
                  minTickGap={40}
                />
                <YAxis
                  yAxisId="bars"
                  orientation="left"
                  tick={AXIS_TICK}
                  tickLine={false}
                  axisLine={false}
                  width={58}
                  tickFormatter={(v) => (metric === "price" ? formatVolume(v) : formatNumber(v))}
                  label={{
                    value: barSeries[0]?.label || "",
                    angle: -90,
                    position: "insideLeft",
                    style: { ...AXIS_TICK, textAnchor: "middle" },
                  }}
                />
                <YAxis
                  yAxisId="lines"
                  orientation="right"
                  tick={AXIS_TICK}
                  tickLine={false}
                  axisLine={false}
                  width={58}
                  domain={["auto", "auto"]}
                  tickFormatter={(v) => formatNumber(v)}
                  label={{
                    value: lineSeries[0]?.label || "",
                    angle: 90,
                    position: "insideRight",
                    style: { ...AXIS_TICK, textAnchor: "middle" },
                  }}
                />
                <Tooltip content={<ChartTooltip />} />
                {barSeries.map((s) =>
                  hidden.has(s.key) ? null : (
                    <Bar key={s.key} yAxisId="bars" dataKey={s.key} name={s.label} fill={BAR_COLOR} isAnimationActive={false} />
                  )
                )}
                {lineSeries.map((s, i) =>
                  hidden.has(s.key) ? null : (
                    <Line
                      key={s.key}
                      yAxisId="lines"
                      type="monotone"
                      dataKey={s.key}
                      name={s.label}
                      stroke={LINE_COLORS[i % LINE_COLORS.length]}
                      strokeWidth={2}
                      strokeDasharray={/median/i.test(s.key) ? "6 4" : undefined}
                      dot={false}
                      connectNulls
                      isAnimationActive={false}
                    />
                  )
                )}
              </ComposedChart>
            </ResponsiveContainer>
          </div>

          <div className="flex flex-wrap justify-center gap-x-6 gap-y-2 mt-5">
            {[...lineSeries, ...barSeries].map((s) => {
              const on = !hidden.has(s.key);
              const color = isBarSeries(s.key) ? BAR_COLOR : LINE_COLORS[lineSeries.indexOf(s) % LINE_COLORS.length];
              return (
                <button key={s.key} type="button" onClick={() => toggle(s.key)} className="inline-flex items-center gap-2 text-sm text-foreground">
                  <span
                    className="w-5 h-5 rounded border-2 flex items-center justify-center"
                    style={{ borderColor: on ? color : "hsl(var(--muted-foreground))", background: on ? color : "transparent" }}
                  >
                    {on && <Check size={14} className="text-white" strokeWidth={3} />}
                  </span>
                  {s.label}
                </button>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
};

export default StockChart;
